import { z } from 'zod';
import { safeFloat } from '@/modules/safe-float';
import { fetchJson } from '@/modules/fetch-core';
import {
  statusFilterPredicate,
  type StatusFilter,
} from '@/modules/market-status';

// Markets API client — read-only v2 market-feed endpoints on the public
// SanR origin in VITE_SCORE_API_BASE (environment precondition; prod vs
// stage is baked in at build time). No auth: requests carry no headers.
// The `filter` query param is a Mongo-style predicate passthrough WITHOUT
// outer braces (`"a":{"$gt":…},"b":…`); the backend queryParser has no
// $in/$eq — use $or of equalities or the first-class params. `take` is
// capped at 500 server-side — never pass more.

const SCORE_API_BASE = import.meta.env.VITE_SCORE_API_BASE;

const KOLProfileSchema = z.looseObject({
  name: z.string(),
  handle: z.string(),
  avatarUrl: z.string(),
});

export const SignalSchema = z.looseObject({
  id: z.string(),
  kol: KOLProfileSchema,
  asset: z.string(),
  slug: z.string().nullable().optional(),
  quoteAsset: z.string(),
  logoUrl: z.string().nullable().optional(),
  direction: z.enum(['up', 'down']),
  title: z.string().optional(),
  description: z.string().optional(),
  tweetText: z.string(),
  // The source text: under isTranslated, tweetText is the English rendering
  // and this is what it was rendered from — the "Translated from" label and
  // the in-place swap read exactly that pair; on other rows it is unread.
  // Both render as sent — the HTML entities X escapes are decoded backend
  // side; don't decode or diff the pair here.
  originalText: z.string(),
  // The translation's source language as ISO 639-1 — meaningful only under
  // isTranslated (before that it holds a pre-check guess or null); read it
  // only there.
  originalLang: z.string().nullable(),
  // The one signal that tweetText is a translation with an original worth
  // showing: true only under a `completed` status whose translator output
  // differs from originalText. `completed` alone is not it — the backend's
  // pre-check misreads terse English tweets (tickers and numbers) as
  // foreign, the translator returns them unchanged, and the run is still
  // filed completed with originalLang 'en' and this flag false; such a row
  // renders as a plain card, never a no-op toggle.
  isTranslated: z.boolean(),
  // Where the backend's translation of tweetText stands, read for the footer
  // notice alone (pending/processing, failed) — whether there is a
  // translation to show is isTranslated's call, never `completed`'s. In
  // every state tweetText renders as sent; what the states mean backend-side
  // is the spec's.
  translationStatus: z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
    'not_needed',
    'skipped',
  ]),
  sourceUrl: z.string(),
  // The tweet's posting moment, null on a row the importer could not date.
  // Served on production and stage but absent from the backend's swagger,
  // so the snapshot gate cannot see it.
  tweetDate: z.string().nullable(),
  tweetDeletedAt: z.string().nullable().optional(),
  tweetImages: z.array(z.string()).optional(),
  imageUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  votingEndsAt: z.string(),
  resolvesAt: z.string(),
  entryPrice: safeFloat,
  // null until the backend resolver writes it — past `resolvesAt` a market
  // can read as resolved by date while this is still null (resolver lag).
  resolvePrice: safeFloat.nullable(),
  resolvedDirection: z.enum(['up', 'down']).nullable().optional(),
  // Ingested outside the public feed (the CSV importer): excluded from the
  // list unless includeHidden is passed, reachable by id; no voting.
  hidden: z.boolean(),
});

// One market row of the /v2/markets feed.
export type Signal = z.output<typeof SignalSchema>;

const SignalsPageSchema = z.looseObject({
  data: z.array(SignalSchema),
  total: safeFloat,
});

type SignalsPage = z.output<typeof SignalsPageSchema>;

const SignalStatusSchema = z.looseObject({
  count: safeFloat,
});

const AssetFilterSchema = z.looseObject({
  ticker: z.string(),
  logoUrl: z.string().nullable(),
});

const KolFilterSchema = z.looseObject({
  handle: z.string(),
});

const SignalFiltersSchema = z.looseObject({
  assets: z.array(AssetFilterSchema),
  kols: z.array(KolFilterSchema),
});

// The shared public-origin fetch-and-parse, also composed by sibling modules
// for other public endpoints on the same origin. Gateway status reports as
// backend 'sanr' — the same origin as the v1/v2 API. The API ships numeric
// values (prices, counts) as STRINGS; schemas coerce them through
// @/modules/safe-float.
export function scoreFetch<S extends z.ZodType>(
  path: string,
  schema: S,
): Promise<z.output<S>> {
  return fetchJson({
    backend: 'sanr',
    source: 'scoreApi',
    key: path,
    url: `${SCORE_API_BASE}${path}`,
    schema,
  });
}

// One page of the markets feed. Sort defaults to
// 'marketStatusOrder:asc,createdAt:desc' — lifecycle tiers first (open →
// voting_closed → resolved), newest within each tier. A caller's `sort`
// replaces the whole string — callers rendering tier-grouped UI (e.g. a
// divider at a tier boundary) must keep the 'marketStatusOrder:asc' prefix,
// the grouping holds only under it (marketStatusOrder is a backend-only
// sortable rank; shipped null, never read as a value). `status` translates
// through @/modules/market-status into the date-predicate filter; a
// `"startedAt":{"$lte":<now>}` clause is ALWAYS appended, so not-yet-started
// markets never appear. assets/usernames send comma-joined (which side of
// the pair an assets ticker matches is backend-owned — results are rendered
// as-is; don't build client-side matching on it). participated/
// notParticipated scope by issuer id to markets the issuer did / did not
// make a prediction in (backend join). Participation = predictions that went
// live (`open`/`closed`) only — `created` (pending trigger orders) and
// `expired` deliberately do not count (the Swagger participated/
// notParticipated descriptions).
export async function getSignals(params?: {
  take?: number;
  skip?: number;
  status?: StatusFilter | null;
  assets?: string[];
  usernames?: string[];
  participated?: string;
  notParticipated?: string;
  sort?: string;
}): Promise<SignalsPage> {
  const qs = new URLSearchParams();
  if (params?.take != null) qs.set('take', String(params.take));
  if (params?.skip != null) qs.set('skip', String(params.skip));
  qs.set('sort', params?.sort ?? 'marketStatusOrder:asc,createdAt:desc');
  const now = new Date();
  const statusPred = statusFilterPredicate(params?.status ?? null, now);
  const startedPred = `"startedAt":{"$lte":"${now.toISOString()}"}`;
  qs.set('filter', [statusPred, startedPred].filter(Boolean).join(','));
  if (params?.assets != null && params.assets.length > 0)
    qs.set('assets', params.assets.join(','));
  if (params?.usernames != null && params.usernames.length > 0)
    qs.set('usernames', params.usernames.join(','));
  if (params?.participated != null && params.participated !== '')
    qs.set('participated', params.participated);
  if (params?.notParticipated != null && params.notParticipated !== '')
    qs.set('notParticipated', params.notParticipated);
  return scoreFetch(`/v2/markets?${qs.toString()}`, SignalsPageSchema);
}

// One market by id — there is no dedicated by-id route, so this reuses the
// feed endpoint with an exact-id filter and take=1. Deliberately no
// startedAt/status clause: a directly-addressed market renders in whatever
// lifecycle state it is in, including not-yet-started (the feed-only guard
// doesn't apply to direct addressing). Resolves null when the id matches
// nothing.
export async function getSignal(id: string): Promise<Signal | null> {
  const qs = new URLSearchParams();
  qs.set('take', '1');
  qs.set('filter', `"id":${JSON.stringify(id)}`);
  const page = await scoreFetch(
    `/v2/markets?${qs.toString()}`,
    SignalsPageSchema,
  );
  return page.data[0] ?? null;
}

// Resolves a short share ref: the id-hash prefix scoped by asset +
// direction + the creation-month window (halved odds of a prefix collision
// ever mattering: colliding markets must also share ticker, direction, and
// month). `$iLike` (Postgres ILIKE) is the ONLY string-match operator
// the queryParser accepts — $like/$regex/$regexp/$iRegexp/$startsWith/
// $substring/$contains all 500 (verified live 2026-08-25); re-test by
// swapping the operator in the filter below against /v2/markets. The asset
// clause is a wildcard-free $iLike, i.e. case-insensitive equality (refs
// carry lowercase tickers, rows uppercase). Inputs arrive grammar-validated
// (alphanumeric asset, 6-hex prefix) — LIKE wildcards are not escaped here.
// take=2 makes ambiguity observable: anything but exactly one match
// resolves null — a hash-prefix collision inside an (asset, direction)
// bucket must dead-link, never open the wrong market.
export async function getSignalByShareRef(lookup: {
  asset: string;
  direction: 'up' | 'down';
  idHashPrefix: string;
  createdWithin: { fromIso: string; beforeIso: string };
}): Promise<Signal | null> {
  const qs = new URLSearchParams();
  qs.set('take', '2');
  qs.set(
    'filter',
    `"id":{"$iLike":${JSON.stringify(`sig-${lookup.idHashPrefix}%`)}},"asset":{"$iLike":${JSON.stringify(lookup.asset)}},"direction":${JSON.stringify(lookup.direction)},"createdAt":{"$gte":${JSON.stringify(lookup.createdWithin.fromIso)},"$lt":${JSON.stringify(lookup.createdWithin.beforeIso)}}`,
  );
  const page = await scoreFetch(
    `/v2/markets?${qs.toString()}`,
    SignalsPageSchema,
  );
  if (page.total !== 1) return null;
  return page.data[0] ?? null;
}

// Total market count; fit for server warm-up probing, not for feed logic.
export async function getSignalStatus(): Promise<{ count: number }> {
  return scoreFetch('/v2/markets/status', SignalStatusSchema);
}

// Count of markets that are new (createdAt > since), still open
// (votingEndsAt > now), and already started; with an issuerId, markets the
// issuer participated in are excluded — an engaged market isn't "new".
export async function getNewOpenMarketsCount(
  since: string,
  issuerId?: string,
): Promise<number> {
  const nowIso = new Date().toISOString();
  const filter = `"createdAt":{"$gt":"${since}"},"votingEndsAt":{"$gt":"${nowIso}"},"startedAt":{"$lte":"${nowIso}"}`;
  const qs = new URLSearchParams();
  qs.set('filter', filter);
  if (issuerId != null && issuerId !== '') qs.set('notParticipated', issuerId);
  qs.set('take', '1');
  const page = await scoreFetch(
    `/v2/markets?${qs.toString()}`,
    SignalsPageSchema,
  );
  return page.total;
}

export function getSignalFilters(): Promise<
  z.output<typeof SignalFiltersSchema>
> {
  return scoreFetch('/v2/markets/filters', SignalFiltersSchema);
}
